import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TablePagination, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, CircularProgress, TextField, Grid,
  FormControl, InputLabel, Select, MenuItem, Card, CardContent } from "@mui/material";
import { Visibility, Cancel, Search, Refresh, Hotel, Download } from "@mui/icons-material";
import axios from "axios";
import { api_urls } from "../../../utils/api-urls";

const HotelBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchBookings();
    fetchStats();
  }, [page, rowsPerPage, statusFilter]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });
      if (statusFilter) params.append("status", statusFilter);
      if (searchText) params.append("search", searchText);

      const response = await axios.get(`${api_urls}api/tbo/admin/hotel-bookings?${params}`);
      if (response.data?.success) {
        setBookings(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching hotel bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${api_urls}api/tbo/admin/booking-stats`);
      if (response.data?.success) {
        setStats(response.data.hotel);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchBookings();
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsOpen(true);
  };

  const handleCancelBooking = async (booking) => {
    if (window.confirm("Are you sure you want to cancel this booking?")) {
      try {
        const response = await axios.post(`${api_urls}api/tbo/hotel/cancel`, {
          bookingId: booking.bookingId || booking._id,
          confirmationNumber: booking.confirmationNumber
        });
        if (response.data?.success) {
          alert("Cancellation request submitted successfully");
          fetchBookings();
        } else {
          alert("Cancellation failed: " + (response.data?.message || "Unknown error"));
        }
      } catch (error) {
        console.error("Cancel error:", error);
        alert("Error cancelling booking");
      }
    }
  };

  const handleDownloadVoucher = async (booking) => {
    try {
      const response = await axios.post(`${api_urls}api/tbo/hotel/generate-voucher`, {
        bookingId: booking.bookingId || booking._id
      });
      if (response.data?.success && response.data?.voucherUrl) {
        window.open(response.data.voucherUrl, "_blank");
      } else {
        alert("Could not generate voucher");
      }
    } catch (error) {
      console.error("Voucher error:", error);
      alert("Error generating voucher");
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "booking_success": case "confirmed": case "completed": return "success";
      case "started": case "payment_initiated": case "pending": return "warning";
      case "payment_cancelled": return "info";
      case "cancelled": case "payment_failed": case "booking_failed": return "error";
      default: return "default";
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      started: "Started",
      payment_initiated: "Payment Initiated",
      payment_cancelled: "Payment Cancelled",
      payment_failed: "Payment Failed",
      booking_success: "Booked",
      booking_failed: "Booking Failed",
      confirmed: "Confirmed",
      completed: "Completed",
      cancelled: "Cancelled"
    };
    return labels[status] || status || "Unknown";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric"
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
        <Hotel sx={{ mr: 1, verticalAlign: "bottom" }} />
        Hotel Bookings
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#e3f2fd" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold">{stats.total}</Typography>
                <Typography variant="caption">Total</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#e8f5e9" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">{stats.booking_success}</Typography>
                <Typography variant="caption">Booked</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#fff3e0" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="warning.main">{stats.payment_initiated}</Typography>
                <Typography variant="caption">In Progress</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#e0f7fa" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="info.main">{stats.payment_cancelled}</Typography>
                <Typography variant="caption">Cancelled Payment</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#ffebee" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="error.main">{stats.payment_failed + stats.booking_failed}</Typography>
                <Typography variant="caption">Failed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#c8e6c9" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">₹{(stats.totalConfirmedAmount || 0).toLocaleString()}</Typography>
                <Typography variant="caption">Confirmed Amount</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#bbdefb" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="primary.main">₹{(stats.adminCommission || 0).toLocaleString()}</Typography>
                <Typography variant="caption">Admin Commission (2%)</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box mb={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search Confirmation, Hotel, City..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          InputProps={{
            startAdornment: <Search color="action" sx={{ mr: 1 }} />
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="started">Started</MenuItem>
            <MenuItem value="payment_initiated">Payment Initiated</MenuItem>
            <MenuItem value="payment_cancelled">Payment Cancelled</MenuItem>
            <MenuItem value="payment_failed">Payment Failed</MenuItem>
            <MenuItem value="booking_success">Booked</MenuItem>
            <MenuItem value="booking_failed">Booking Failed</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleSearch}>Search</Button>
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => { setSearchText(""); setStatusFilter(""); fetchBookings(); }}>
          Reset
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#ff9800" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Confirmation</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>User</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Hotel</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>City</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Check-in</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Amount</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary" py={3}>No hotel bookings found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking, index) => (
                  <TableRow key={booking._id || index} hover>
                    <TableCell>
                      <Typography fontWeight="bold">{booking.confirmationNumber || booking._id?.slice(-8) || "-"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{booking.userId?.firstname} {booking.userId?.lastname}</Typography>
                      <Typography variant="caption" color="textSecondary">{booking.userId?.mobile}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="medium">{booking.hotelName || "-"}</Typography>
                    </TableCell>
                    <TableCell>{booking.hotelCity || "-"}</TableCell>
                    <TableCell>{formatDate(booking.checkIn)}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">₹{booking.amount?.toLocaleString() || "-"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(booking.status)} 
                        color={getStatusColor(booking.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleViewDetails(booking)}
                        title="View Details"
                        size="small"
                      >
                        <Visibility />
                      </IconButton>
                      {booking.status === "booking_success" && (
                        <>
                          <IconButton 
                            color="success" 
                            onClick={() => handleDownloadVoucher(booking)}
                            title="Download Voucher"
                            size="small"
                          >
                            <Download />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleCancelBooking(booking)}
                            title="Cancel Booking"
                            size="small"
                          >
                            <Cancel />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      {/* Booking Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: "#ff9800", color: "white" }}>
          <Typography variant="h6" fontWeight="bold">
            <Hotel sx={{ mr: 1, verticalAlign: "bottom" }} />
            Hotel Booking Details
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBooking && (
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Confirmation No</Typography>
                <Typography fontWeight="bold" fontSize="1.1rem">{selectedBooking.confirmationNumber || "-"}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box>
                  <Chip label={getStatusLabel(selectedBooking.status)} color={getStatusColor(selectedBooking.status)} />
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Amount</Typography>
                <Typography fontWeight="bold" fontSize="1.2rem">₹{selectedBooking.amount?.toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Created</Typography>
                <Typography>{formatDateTime(selectedBooking.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12}><hr /></Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Hotel Name</Typography>
                <Typography fontWeight="bold">{selectedBooking.hotelName || "-"}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">City</Typography>
                <Typography>{selectedBooking.hotelCity || "-"}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Room Type</Typography>
                <Typography>{selectedBooking.roomType || "-"}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Check-in</Typography>
                <Typography>{formatDate(selectedBooking.checkIn)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Check-out</Typography>
                <Typography>{formatDate(selectedBooking.checkOut)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Nights</Typography>
                <Typography>{selectedBooking.nights || "-"}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Guests</Typography>
                <Typography>{selectedBooking.guestCount || "-"}</Typography>
              </Grid>
              <Grid item xs={12}><hr /></Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="textSecondary">User</Typography>
                <Typography>{selectedBooking.userId?.firstname} {selectedBooking.userId?.lastname}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="textSecondary">Mobile</Typography>
                <Typography>{selectedBooking.userId?.mobile || "-"}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="textSecondary">Email</Typography>
                <Typography>{selectedBooking.userId?.email || "-"}</Typography>
              </Grid>
              {selectedBooking.razorpayOrderId && (
                <>
                  <Grid item xs={12}><hr /></Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Razorpay Order ID</Typography>
                    <Typography variant="body2">{selectedBooking.razorpayOrderId}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Razorpay Payment ID</Typography>
                    <Typography variant="body2">{selectedBooking.razorpayPaymentId || "-"}</Typography>
                  </Grid>
                </>
              )}
              {selectedBooking.errorMessage && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Error Message</Typography>
                  <Typography color="error">{selectedBooking.errorMessage}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedBooking?.status === "booking_success" && (
            <Button 
              onClick={() => handleDownloadVoucher(selectedBooking)} 
              color="success" 
              variant="outlined"
              startIcon={<Download />}
            >
              Download Voucher
            </Button>
          )}
          <Button onClick={() => setDetailsOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HotelBookings;
